<script setup lang="ts">
import type { VxeGridProps } from '#/adapter/vxe-table';

import { computed, ref } from 'vue';

import { EllipsisText, useVbenDrawer, VbenButton } from '@vben/common-ui';
import { useUserStore } from '@vben/stores';

import {
  ElBadge,
  ElButton,
  ElDivider,
  ElMessage,
  ElMessageBox,
  ElTabPane,
  ElTabs,
} from 'element-plus';

import { useVbenVxeGrid } from '#/adapter/vxe-table';
import {
  downloadOrderTemplateApi,
  getChannelListApi,
  importNewOrdersCheckApi,
  importNewOrdersDoApi,
} from '#/api';
import DownloadFile from '#/components/file/download.vue';
import FileUpload from '#/components/file/upload.vue';
import FieldTemplateGrid from '#/components/order/FieldTemplateGrid/index.vue';
import { createSetCostThreshold } from '#/components/transferHouse/createSetCostThreshold.jsx';
import { useDicEnumStore } from '#/store';

const dicEnumStore = useDicEnumStore();
const { order_type_list, proxy_mode_list, shipping_label_list } =
  dicEnumStore.dicEnum;
const userStore = useUserStore();
const loading = ref(false);
const showCloseTip = ref(false);
function initData() {
  purchaseChannelId.value = '1';
}
interface RowType {}
// 初始化抽屉组件
const [Drawer, drawerApi] = useVbenDrawer({
  appendToMain: true,
  class: 'w-full',
  confirmText: '提交订单',
  onOpenChange(isOpen: boolean) {
    showCloseTip.value = false;
    if (isOpen) {
      drawerApi.setData({ refresh: false });
      initData();
      getChannelListApi().then((res) => {
        channelList.value = res.filter(
          (item: Record<string, any>) => item.enableStatus,
        );
      });
    }
  },
  onConfirm() {
    showCloseTip.value = false;
    confirmSubmitOrder();
  },
  async onBeforeClose() {
    if (showCloseTip.value) {
      return ElMessageBox.confirm('退出后，表格数据将会清空', '确认退出', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          resetTable();
          return true;
        })
        .catch(() => {
          return false;
        });
    } else {
      resetTable();
    }
  },
});

// 初始化表格
const activeName = ref('successList');
const failListData = ref([]);
const successListData = ref([]);

const purchaseChannelId = ref('1');

// 面单方式：0.第三方 2.自主上传
const shippingLabelType = ref('2');

const gridOptions: VxeGridProps<RowType> = {
  autoResize: true,
  height: '100%',
  columns: [
    {
      title: '经营平台信息',
      field: 'extraPlatformInfo',
      slots: { default: 'extraPlatformInfo' },
      showOverflow: false,
    },
    {
      title: '买家信息',
      field: 'receiverInfo',
      slots: { default: 'receiverInfo' },
      showOverflow: false,
    },
    {
      title: '采购商品信息',
      field: 'platformProductInfo',
      slots: { default: 'platformProductInfo' },
      showOverflow: false,
    },
    {
      title: '订单留言',
      field: 'customerNote',
      slots: { default: 'customerNote' },
      showOverflow: false,
    },
  ],
  data: [],
  keepSource: true,
  pagerConfig: {},
};
const [SuccessGrid, successGridApi] = useVbenVxeGrid({
  gridOptions,
});
const getFailGridColumns = () => {
  const reason: RowType[] = [
    {
      title: '原因',
      field: 'reason',
      slots: { default: 'reason' },
      showOverflow: false,
    },
  ];
  if (gridOptions.columns) {
    return [...reason, ...gridOptions.columns];
  }
};
const [FailGrid, failGridApi] = useVbenVxeGrid({
  gridOptions: {
    ...gridOptions,
    columns: getFailGridColumns(),
  },
});

// 清空表格总数
function resetTable() {
  loadTableData(failGridApi);
  loadTableData(successGridApi);
  successListData.value = [];
  failListData.value = [];
}
// 表格用过更新配置的方式加载数据
function loadTableData(gridApi: Record<string, any>, data = []) {
  gridApi.setGridOptions({
    data,
    pagerConfig: {
      total: data.length,
    },
  });
}

// 上传文件
const fileList = ref([]);

// 组合失败的表格数据用于回显错误原因和样式
function assembleTableData(failList: Record<string, any>) {
  return failList.map((item: Record<string, any>) => {
    return {
      ...item.rowData,
      errors: item.errors,
      errorsText: item.errors.join('\r\n'), // 用于导出
      failCells: item.failCells,
    };
  });
}

const uploadFile = (file: Record<string, any>) => {
  loading.value = true;
  successGridApi.grid.loadData([]);
  failGridApi.grid.loadData([]);
  failListData.value = [];
  successListData.value = [];

  importNewOrdersCheckApi(file)
    .then((res) => {
      const { successList, failList } = res;
      successListData.value = successList;
      failListData.value = assembleTableData(failList);
      successGridApi.setGridOptions({
        data: successList,
        pagerConfig: {
          total: successList.length,
        },
      });

      // successGridApi.grid.loadData(successList);
      failGridApi.setGridOptions({
        pagerConfig: {
          total: failListData.value.length,
        },
        data: failListData.value,
      });

      // failGridApi.grid.loadData(failListData.value);

      columnMappings.value = res.columnMappings;
      ElMessage.success('导入成功！');
      showCloseTip.value = true;
    })
    .finally(() => {
      loading.value = false;
      fileList.value = [];
    });
};
function onChange(file: Record<string, any>) {
  if (file.status === 'ready') {
    uploadFile({ file: file.raw });
  }
}

// 新增：定义列映射类型（根据实际业务字段调整）

let columnMappings = ref<Record<string, any>[]>([]);

const confirmSubmitOrder = () => {
  if (successListData.value.length === 0) {
    ElMessage.error('请上传订单');
    return;
  }
  ElMessageBox.alert(
    '<strong>确认是否提交进行采购？</strong><br><p>- 只会将导入成功的提交，未导入成功的不会被提交</p><p>- 提交后，如有问题也可以自行撤销后再提交。</p>',
    '提交操作',
    {
      // if you want to disable its autofocus
      // autofocus: false,
      confirmButtonText: '确认提交',
      cancelButtonText: '取消',
      showCancelButton: true,
      dangerouslyUseHTMLString: true,
    },
  )
    .then(() => {
      submitOrder(successListData.value);
    })
    .catch(() => {});
};
const submitOrder = (successList: Record<string, any>) => {
  const params: any = {
    successList,
    purchaseChannelId: purchaseChannelId.value,
    orderType: orderType.value,
    orderMode: orderMode.value,
  };
  if (orderType.value == 1) {
    params.shippingLabelType = shippingLabelType.value;
  }
  importNewOrdersDoApi(params)
    .then(() => {
      ElMessage.success('提交成功！');
      drawerApi.setData({ refresh: true }).close();
    })
    .finally(() => {});
};

// 获取渠道列表
interface Channel {
  channelImgUrl: string;
  id: string;
  name: string;
}
const channelList = ref<Channel[]>([]);

interface DeliverGoodsType {
  value: number;
  label: string;
}
const orderType = ref(0);

const orderMode = ref(0);
const canSelfPurchase = computed(
  () => userStore.userInfo?.showMerchantPlugin === true,
);
const visibleProxyModeList = computed(() =>
  canSelfPurchase.value
    ? proxy_mode_list
    : proxy_mode_list.filter((item: { value: number }) => item.value !== 1),
);

const deliverGoodsTypeList = ref<DeliverGoodsType[]>(order_type_list);

const [SetCostThresholdBtn, SetCostThresholdModal] = createSetCostThreshold();

function selectOrderMode(value: number) {
  orderMode.value = value;
  if (orderMode.value === 1) {
    purchaseChannelId.value = '1';
  }
}
</script>
<template>
  <div>
    <Drawer title="批量上传订单" :loading="loading">
      <div class="flex items-center justify-between">
        <div class="flex">
          <div class="">
            <p class="pb-1 text-sm text-blue-500">请先选择采购模式</p>
            <ElButton
              v-for="proxy_mode in visibleProxyModeList"
              :type="orderMode === proxy_mode.value ? 'primary' : ''"
              @click="selectOrderMode(proxy_mode.value)"
              :key="proxy_mode.value"
              class="mb-1"
            >
              {{ proxy_mode.label }}
            </ElButton>
          </div>
          <div class="ml-6">
            <p class="pb-1 text-sm text-blue-500">请先选择订单的代采渠道</p>
            <ElButton
              v-for="channel in channelList"
              :type="purchaseChannelId === channel.id ? 'primary' : ''"
              @click="purchaseChannelId = channel.id"
              :key="channel.id"
              class="mb-1"
              :disabled="orderMode === 1"
            >
              {{ channel.name }}
            </ElButton>
          </div>

          <div class="ml-6">
            <p class="pb-1 text-sm text-blue-500">请先选择发货模式</p>
            <ElButton
              v-for="deliverGoodsTypeItem in deliverGoodsTypeList"
              :type="orderType === deliverGoodsTypeItem.value ? 'primary' : ''"
              @click="orderType = deliverGoodsTypeItem.value"
              :key="deliverGoodsTypeItem.value"
              class="mb-1"
            >
              {{ deliverGoodsTypeItem.label }}
            </ElButton>
          </div>

          <div class="ml-6" v-if="orderType == 1">
            <p class="pb-1 text-sm text-blue-500">请选择购买面单方式</p>
            <ElButton
              v-for="item in shipping_label_list"
              :type="shippingLabelType === item.value ? 'primary' : ''"
              @click="shippingLabelType = item.value"
              :key="item.value"
              class="mb-1"
            >
              {{ item.label }}
            </ElButton>
          </div>
        </div>
        <div v-show="orderType === 1 && shippingLabelType === '0'">
          <SetCostThresholdBtn />
        </div>
      </div>
      <ElDivider border-style="dashed" />
      <div class="flex justify-between">
        <div class="flex">
          <FileUpload
            v-model:file-list="fileList"
            :multiple="false"
            :on-change="onChange"
          >
            <VbenButton>上传订单文档</VbenButton>
          </FileUpload>
          <VbenButton class="ml-1" @click="downloadOrderTemplateApi">
            下载订单文档模板
          </VbenButton>
        </div>

        <DownloadFile
          type="primary"
          :data="{
            data: activeName === 'failList' ? failListData : successListData,
            header:
              activeName === 'failList'
                ? [{ errorsText: '错误原因' }, ...columnMappings]
                : columnMappings,
          }"
          :file-name="`${activeName === 'failList' ? '识别失败' : '识别成功'}订单`"
        >
          导出记录
        </DownloadFile>
      </div>
      <div>
        <ElTabs v-model="activeName">
          <ElTabPane
            label="识别成功"
            name="successList"
            style="height: calc(100vh - 440px)"
          >
            <template #label>
              <span class="custom-tabs-label">
                识别成功
                <ElBadge
                  v-if="successListData.length > 0"
                  class="mark"
                  :value="successListData.length"
                  :max="successListData.length + 2"
                />
              </span>
            </template>
            <div style="height: 100%; overflow: hidden">
              <SuccessGrid>
                <template #extraPlatformInfo="{ row }">
                  <FieldTemplateGrid field="extraPlatformInfo" :row="row" />
                </template>
                <template #receiverInfo="{ row }">
                  <FieldTemplateGrid field="receiverInfo" :row="row" />
                </template>
                <template #platformProductInfo="{ row }">
                  <FieldTemplateGrid field="platformProductInfo" :row="row" />
                </template>
                <template #messageInformation="{ row }">
                  <FieldTemplateGrid field="messageInformation" :row="row" />
                </template>
                <template #customerNote="{ row }">
                  <EllipsisText :line="3">{{ row.customerNote }}</EllipsisText>
                </template>
              </SuccessGrid>
            </div>
          </ElTabPane>
          <ElTabPane
            label="识别失败"
            name="failList"
            style="height: calc(100vh - 440px)"
          >
            <template #label>
              <span class="custom-tabs-label">
                识别失败
                <ElBadge
                  v-if="failListData.length > 0"
                  class="mark"
                  :value="failListData.length"
                  :max="failListData.length + 2"
                />
              </span>
            </template>
            <div style="height: 100%; overflow: hidden">
              <FailGrid>
                <template #reason="{ row }">
                  <ul class="m-auto list-decimal pl-3 text-left">
                    <li v-for="(error, index) in row.errors" :key="index">
                      {{ error }}
                    </li>
                  </ul>
                </template>
                <template #extraPlatformInfo="{ row }">
                  <FieldTemplateGrid field="extraPlatformInfo" :row="row" />
                </template>
                <template #receiverInfo="{ row }">
                  <FieldTemplateGrid field="receiverInfo" :row="row" />
                </template>
                <template #platformProductInfo="{ row }">
                  <FieldTemplateGrid field="platformProductInfo" :row="row" />
                </template>
                <template #messageInformation="{ row }">
                  <FieldTemplateGrid field="messageInformation" :row="row" />
                </template>
                <template #customerNote="{ row }">
                  <EllipsisText :line="3">{{ row.customerNote }}</EllipsisText>
                </template>
              </FailGrid>
            </div>
          </ElTabPane>
        </ElTabs>
      </div>

      <SetCostThresholdModal title="设置费用阈值" />
    </Drawer>
  </div>
</template>
